import { useState, useEffect, useRef, useCallback } from 'react';
import { IEpisode, VideoEditConfig } from '@packages/global';

interface UseBgmPlayerOptions {
  episode: IEpisode | null;
  editConfig: VideoEditConfig | null;
  isPlaying: boolean;
  currentTime: number;
}

export function useBgmPlayer({ episode, editConfig, isPlaying, currentTime }: UseBgmPlayerOptions) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // 获取 BGM 配置
  const bgmEnabled = editConfig?.tracks?.bgm?.enabled ?? true;
  const bgmVolume = (editConfig?.tracks?.bgm?.volume ?? 0.3) * 0.6; // 与导出保持一致
  const totalDuration = editConfig?.timeline?.totalDuration ?? 0;
  const bgmUrl = episode?.bgmUrl;

  // 是否应该播放 BGM
  const shouldPlay = isPlaying && bgmEnabled && !!bgmUrl && currentTime < totalDuration;

  // 创建或更新音频元素
  useEffect(() => {
    if (!bgmUrl) {
      // 没有 BGM URL，清理音频
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
        setIsLoaded(false);
      }
      return;
    }

    // 如果 URL 变化，重新创建音频
    if (!audioRef.current || audioRef.current.src !== bgmUrl) {
      if (audioRef.current) {
        audioRef.current.pause();
      }

      const audio = new Audio(bgmUrl);
      audio.loop = true;
      audio.volume = bgmVolume;

      audio.addEventListener('loadedmetadata', () => {
        setIsLoaded(true);
      });

      audio.addEventListener('error', (e) => {
        console.error('BGM 加载失败:', e);
        setIsLoaded(false);
      });

      audioRef.current = audio;
    }

    return () => {
      // 组件卸载时清理
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
        setIsLoaded(false);
      }
    };
  }, [bgmUrl]);

  // 更新音量
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = bgmVolume;
    }
  }, [bgmVolume]);

  // 控制播放/暂停
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (shouldPlay) {
      // 同步播放位置（考虑循环）
      if (isLoaded && audio.duration > 0) {
        const loopedTime = currentTime % audio.duration;
        // 只有当时间差异较大时才同步，避免频繁 seek
        if (Math.abs(audio.currentTime - loopedTime) > 0.5) {
          audio.currentTime = loopedTime;
        }
      }

      if (audio.paused) {
        audio.play().catch(err => {
          console.error('BGM 播放失败:', err);
        });
      }
    } else {
      if (!audio.paused) {
        audio.pause();
      }
    }
  }, [shouldPlay, isLoaded]);

  // 暴露手动停止方法
  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, []);

  return {
    isLoaded,
    stop
  };
}
